// Shared pixel-earth atlas used by every art pack's `drawPixelatedEarth`.
//
// Encodes a 60×30 equirectangular grid. Each cell is 6° × 6° on the
// globe, so col 0 = lon -180°, col 59 ≈ +174°; row 0 = lat +90°,
// row 29 ≈ -84°. Continents were hand-placed from real coordinates so
// the matchmaker map reads as Earth instead of a generic blob.
//
// Cell values: 0 = ocean, 1 = land, 2 = mountain/highland.
//
// Packs should render at y = r * CELL (no offset) and loop r over
// [0, EARTH_ROWS) and c over [0, EARTH_COLS). The UV anchors in
// `server_query.ts` are 0..1 fractions, so they stay valid regardless
// of the cell size once the grid spans the full canvas.

export const EARTH_COLS = 60;
export const EARTH_ROWS = 30;

type Span = [row: number, colStart: number, colEnd: number];

// Each span paints one horizontal strip of the named region. Ordering
// is by continent, not by row — later spans overwrite earlier ones.
const LAND_SPANS: Span[] = [
  // --- North America ---
  [2,  6, 19],                       // Canadian Arctic archipelago
  [3,  2, 20],                       // Yukon + Hudson Bay rim
  [4,  1, 21],
  [5,  1, 20],
  [6,  2, 20],                       // USA/Canada border
  [7,  3, 20],
  [8,  5, 19],
  [9,  8, 19],                       // USA south
  [10, 10, 18],                      // Mexico top
  [11, 11, 16],                      // Mexico body
  [12, 13, 16],                      // Central America
  [13, 15, 17],                      // Panama

  // --- Greenland ---
  [1, 24, 27],
  [2, 22, 28],
  [3, 22, 27],
  [4, 23, 26],
  [5, 24, 25],

  // --- South America ---
  [13, 18, 22],                      // Colombia / Venezuela
  [14, 17, 23],                      // north Brazil + Guianas
  [15, 17, 23],
  [16, 17, 22],                      // Brazil body
  [17, 17, 22],
  [18, 17, 21],
  [19, 17, 20],                      // Argentina
  [20, 17, 20],
  [21, 17, 19],                      // Patagonia
  [22, 17, 18],
  [23, 18, 18],                      // Cape Horn dot

  // --- Europe (west half of Eurasia) ---
  [3, 30, 36],                       // Scandinavia top
  [4, 29, 37],
  [5, 28, 38],                       // UK/Ireland + Baltic + Russia entry
  [6, 28, 39],
  [7, 28, 41],
  [8, 28, 42],
  [9, 28, 42],

  // --- Africa ---
  [9,  27, 39],                      // Maghreb + Egypt coast
  [10, 27, 40],                      // Sahara
  [11, 27, 39],
  [12, 28, 39],
  [13, 29, 38],                      // Sahel / Nigeria
  [14, 30, 38],
  [15, 30, 38],                      // Congo basin
  [16, 31, 37],
  [17, 32, 37],                      // southern Africa
  [18, 32, 37],
  [19, 33, 36],
  [20, 33, 35],                      // Cape
  // Madagascar
  [17, 38, 39],
  [18, 38, 39],

  // --- Asia (east half of Eurasia) ---
  [3, 36, 58],                       // Siberian arctic coast
  [4, 35, 58],
  [5, 35, 58],
  [6, 36, 58],
  [7, 37, 58],
  // Middle East / Caucasus
  [9,  40, 43],
  [10, 40, 43],
  [11, 40, 43],
  // Arabia
  [10, 38, 42],
  [11, 38, 42],
  [12, 38, 42],
  // India
  [9,  43, 46],
  [10, 43, 46],
  [11, 43, 46],
  [12, 43, 45],
  [13, 43, 44],
  // China / Mongolia
  [7, 43, 55],
  [8, 43, 54],
  [9, 44, 54],
  [10, 45, 54],
  [11, 47, 54],
  // Japan
  [7, 54, 55],
  [8, 54, 55],
  [9, 54, 55],
  // SE Asia mainland
  [11, 47, 51],
  [12, 46, 51],
  [13, 46, 52],
  // Indonesia
  [13, 46, 53],
  [14, 46, 54],
  [15, 47, 54],
  // Papua New Guinea
  [15, 53, 56],
  [16, 53, 56],
  // Philippines
  [11, 52, 53],
  [12, 52, 53],

  // --- Australia + Oceania ---
  [17, 48, 55],
  [18, 47, 55],
  [19, 48, 55],
  [20, 49, 54],
  // Tasmania
  [21, 51, 52],
  // New Zealand
  [20, 57, 58],
  [21, 57, 58],

  // --- Antarctica ---
  [26, 0, 59],
  [27, 0, 59],
  [28, 0, 59],
  [29, 8, 55],

  // --- Arctic islands ---
  [2, 37, 38],                       // Svalbard
  [2, 40, 42],                       // Severnaya Zemlya
  [2, 44, 47],                       // New Siberian Islands
  [2, 55, 56],                       // Wrangel
];

// Mountain cells — only applied where there's already land.
const MOUNTAIN_SPANS: Span[] = [
  // Rockies
  [6, 5, 7], [7, 5, 7], [8, 6, 8], [9, 8, 9],
  // Andes
  [15, 17, 18], [16, 17, 18], [17, 17, 18], [18, 17, 18], [19, 17, 18],
  // Alps / Caucasus / Zagros
  [7, 31, 36], [8, 39, 42],
  // Urals
  [4, 37, 37], [5, 37, 37], [6, 37, 37], [7, 37, 37],
  // Himalayas + Tibetan plateau
  [9, 44, 48], [10, 44, 49],
  // East African highlands + Ethiopia
  [14, 36, 37], [15, 36, 37],
  // New Guinea highlands
  [16, 54, 55],
  // Great Dividing Range (Australia)
  [18, 54, 55],
];

function buildGrid(): Uint8Array {
  const g = new Uint8Array(EARTH_COLS * EARTH_ROWS);
  for (const [r, c0, c1] of LAND_SPANS) {
    if (r < 0 || r >= EARTH_ROWS) continue;
    for (let c = c0; c <= c1; c++) {
      if (c < 0 || c >= EARTH_COLS) continue;
      g[r * EARTH_COLS + c] = 1;
    }
  }
  for (const [r, c0, c1] of MOUNTAIN_SPANS) {
    if (r < 0 || r >= EARTH_ROWS) continue;
    for (let c = c0; c <= c1; c++) {
      if (c < 0 || c >= EARTH_COLS) continue;
      if (g[r * EARTH_COLS + c] === 1) g[r * EARTH_COLS + c] = 2;
    }
  }
  return g;
}

const EARTH_GRID = buildGrid();

// Convenience: packs call this with (r, c) for a single cell.
export function earthCell(r: number, c: number): 0 | 1 | 2 {
  if (r < 0 || r >= EARTH_ROWS || c < 0 || c >= EARTH_COLS) return 0;
  return EARTH_GRID[r * EARTH_COLS + c] as 0 | 1 | 2;
}

// String-atlas view for packs that already loop over an atlas of
// character cells. Row is a 60-char string where each character is
// '0', '1' or '2'. Indexing an out-of-bounds cell returns '0' via
// JavaScript's string semantics (undefined → compare fails), so the
// existing neighbour-probe patterns in packs keep working.
export const EARTH_ATLAS: string[] = (() => {
  const out: string[] = [];
  for (let r = 0; r < EARTH_ROWS; r++) {
    let row = '';
    for (let c = 0; c < EARTH_COLS; c++) {
      row += String(EARTH_GRID[r * EARTH_COLS + c]);
    }
    out.push(row);
  }
  return out;
})();
